import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Alert,
    App,
    Button,
    Card,
    Col,
    Form,
    Input,
    List,
    Row,
    Space,
    Tag,
    Typography,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { PageLayout } from '../../components/layout'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import { usePermissions } from '../../hooks/usePermissions'
import {
    getPricingSettings,
    updatePricingSettings,
    type PricingSettings,
} from '../../services/systemService'
import { getDevicePackageDisplayName } from '../../utils/devicePackageDisplay'
import type { MenderConfigUpdate } from '../../types/device-upgrade'
import { defaultMenderFormValues, MenderOtaConfigPanel } from './MenderOtaSettings'

const { Text } = Typography
const SYSTEM_SETTINGS_I18N_PREFIX = 'admin:systemSettings'

type SettingSectionKey =
    | 'pricing'
    | 'alerts'
    | 'menderOta'
    | 'homepage'
    | 'deviceRules'
    | 'security'

interface SettingCategory {
    key: SettingSectionKey
    enabled: boolean
    visible: boolean
}

type PricingSettingsFormValues = {
    foundation?: Partial<Record<PricingFieldKey, number | string>>
    recognition?: Partial<Record<PricingFieldKey, number | string>>
    insight?: Partial<Record<PricingFieldKey, number | string>>
    reminders?: Partial<Record<ReminderFieldKey, number | string>>
}

const packageKeys = ['foundation', 'recognition', 'insight'] as const
type PricingFieldKey = keyof PricingSettings['foundation']
type ReminderFieldKey = keyof PricingSettings['reminders']

const amountFieldKeys = ['deviceFee', 'cloudPlatformFee'] as const
const amountPattern = /^\d+(?:\.\d{1,2})?$/
const maxAmount = 999999.99
const reminderDayPattern = /^\d+$/
const reminderFieldLimits: Record<ReminderFieldKey, { max: number; messageKey: string }> = {
    lureExpireDays: {
        max: 30,
        messageKey: 'invalidReminderDays',
    },
    lureValidityDays: {
        max: 9999,
        messageKey: 'invalidLureValidityDays',
    },
    feeDueReminderDays: {
        max: 30,
        messageKey: 'invalidReminderDays',
    },
}

const settingBlockStyle: React.CSSProperties = {
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    padding: 16,
    minHeight: '100%',
}

const defaultSettings: PricingSettings = {
    foundation: {
        deviceFee: 6800,
        cloudPlatformFee: 199,
    },
    recognition: {
        deviceFee: 13800,
        cloudPlatformFee: 299,
    },
    insight: {
        deviceFee: 23600,
        cloudPlatformFee: 499,
    },
    reminders: {
        lureExpireDays: 5,
        lureValidityDays: 30,
        feeDueReminderDays: 5,
    },
}

const systemConfigSettingCategories = [
    {
        key: 'pricing',
        enabled: false,
    },
    {
        key: 'alerts',
        enabled: true,
    },
    {
        key: 'homepage',
        enabled: false,
    },
    {
        key: 'deviceRules',
        enabled: false,
    },
    {
        key: 'security',
        enabled: false,
    },
] as const

const settingSectionKeys: SettingSectionKey[] = [
    'pricing',
    'alerts',
    'menderOta',
    'homepage',
    'deviceRules',
    'security',
]

const getSettingSectionFromSearch = (value: string | null): SettingSectionKey | null => (
    value && settingSectionKeys.includes(value as SettingSectionKey)
        ? value as SettingSectionKey
        : null
)

const usesPricingSettings = (section: SettingSectionKey): boolean => (
    section === 'pricing' || section === 'alerts'
)

const mergeSettings = (
    baseSettings: PricingSettings,
    formValues: PricingSettingsFormValues,
): PricingSettings => ({
    foundation: {
        ...baseSettings.foundation,
        ...formValues.foundation,
    } as PricingSettings['foundation'],
    recognition: {
        ...baseSettings.recognition,
        ...formValues.recognition,
    } as PricingSettings['recognition'],
    insight: {
        ...baseSettings.insight,
        ...formValues.insight,
    } as PricingSettings['insight'],
    hardwareVersions: baseSettings.hardwareVersions,
    reminders: {
        ...baseSettings.reminders,
        ...formValues.reminders,
    } as PricingSettings['reminders'],
})

const formatAmount = (value: unknown): string => {
    const amount = Number(value)
    return Number.isFinite(amount) ? amount.toFixed(2) : ''
}

const formatPricingSettingsForForm = (settings: PricingSettings): PricingSettingsFormValues => ({
    foundation: {
        deviceFee: formatAmount(settings.foundation.deviceFee),
        cloudPlatformFee: formatAmount(settings.foundation.cloudPlatformFee),
    },
    recognition: {
        deviceFee: formatAmount(settings.recognition.deviceFee),
        cloudPlatformFee: formatAmount(settings.recognition.cloudPlatformFee),
    },
    insight: {
        deviceFee: formatAmount(settings.insight.deviceFee),
        cloudPlatformFee: formatAmount(settings.insight.cloudPlatformFee),
    },
    reminders: settings.reminders,
})

const parseAmount = (value: unknown): number => Number(String(value).trim())
const parseReminderDays = (value: unknown): number => Number(String(value).trim())

const normalizePricingFormValues = (
    values: PricingSettingsFormValues,
): PricingSettingsFormValues => {
    const normalizedValues: PricingSettingsFormValues = {
        ...values,
        reminders: values.reminders ? { ...values.reminders } : values.reminders,
    }

    packageKeys.forEach((packageKey) => {
        const packageValues = values[packageKey]
        if (!packageValues) {
            return
        }
        normalizedValues[packageKey] = { ...packageValues }
        amountFieldKeys.forEach((fieldKey) => {
            const value = packageValues[fieldKey]
            if (value !== undefined) {
                normalizedValues[packageKey]![fieldKey] = parseAmount(value)
            }
        })
    })

    if (normalizedValues.reminders) {
        ;(Object.keys(reminderFieldLimits) as ReminderFieldKey[]).forEach((fieldKey) => {
            const value = normalizedValues.reminders?.[fieldKey]
            if (value !== undefined) {
                normalizedValues.reminders![fieldKey] = parseReminderDays(value)
            }
        })
    }

    return normalizedValues
}

const isValidAmount = (value: unknown): boolean => {
    const rawValue = String(value ?? '').trim()
    if (!amountPattern.test(rawValue)) {
        return false
    }
    const amount = Number(rawValue)
    return Number.isFinite(amount) && amount > 0 && amount <= maxAmount
}

const isValidReminderDays = (value: unknown, max: number): boolean => {
    const rawValue = String(value ?? '').trim()
    if (!reminderDayPattern.test(rawValue)) {
        return false
    }
    const days = Number(rawValue)
    return Number.isSafeInteger(days) && days >= 1 && days <= max
}

const SystemSettings: React.FC = () => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['admin', 'navigation', 'device'])
    const { canReadMenderOtaConfig, hasPermission } = usePermissions()
    const [searchParams, setSearchParams] = useSearchParams()
    const [form] = Form.useForm<PricingSettingsFormValues>()
    const requestedSection = getSettingSectionFromSearch(searchParams.get('section'))
    const [activeSection, setActiveSection] = useState<SettingSectionKey>(requestedSection || 'pricing')
    const [settings, setSettings] = useState<PricingSettings>(defaultSettings)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [pricingSettingsLoaded, setPricingSettingsLoaded] = useState(false)
    const [menderOtaDraftValues, setMenderOtaDraftValues] = useState<MenderConfigUpdate>(defaultMenderFormValues)
    const [menderOtaDraftDirty, setMenderOtaDraftDirty] = useState(false)
    const [menderOtaPreserveDraftOnMount, setMenderOtaPreserveDraftOnMount] = useState(false)
    const canReadSystemSettings = hasPermission('system:config:read') || hasPermission('system:config')
    const canSaveSettings = hasPermission('system:config:update')
    const settingCategories = useMemo<SettingCategory[]>(() => {
        const categories: SettingCategory[] = []

        if (canReadSystemSettings) {
            systemConfigSettingCategories
                .filter((category) => category.key === 'pricing' || category.key === 'alerts')
                .forEach((category) => {
                    categories.push({
                        ...category,
                        visible: true,
                    })
                })
        }

        if (canReadMenderOtaConfig) {
            categories.push({
                key: 'menderOta',
                enabled: true,
                visible: true,
            })
        }

        if (canReadSystemSettings) {
            systemConfigSettingCategories
                .filter((category) => category.key !== 'pricing' && category.key !== 'alerts')
                .forEach((category) => {
                    categories.push({
                        ...category,
                        visible: true,
                    })
                })
        }

        return categories
    }, [canReadMenderOtaConfig, canReadSystemSettings])
    const firstAvailableSection = settingCategories[0]?.key || 'pricing'
    const selectedCategory = useMemo(
        () => settingCategories.find((category) => category.key === activeSection),
        [activeSection, settingCategories],
    )
    const canLoadPricingSettings = canReadSystemSettings
        && selectedCategory?.enabled !== false
        && usesPricingSettings(activeSection)

    const loadSettings = useCallback(async () => {
        setLoading(true)
        try {
            const loadedSettings = await getPricingSettings()
            setSettings(loadedSettings)
            form.setFieldsValue(formatPricingSettingsForForm(loadedSettings))
        } catch (error) {
            console.error(error)
            setSettings(defaultSettings)
            form.setFieldsValue(formatPricingSettingsForForm(defaultSettings))
            message.error(t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.loadFailed`))
        } finally {
            setPricingSettingsLoaded(true)
            setLoading(false)
        }
    }, [form, message, t])

    useEffect(() => {
        const nextSection = requestedSection
            && settingCategories.some((category) => category.key === requestedSection && category.visible)
            ? requestedSection
            : firstAvailableSection
        setActiveSection((currentSection) => (
            currentSection === nextSection ? currentSection : nextSection
        ))
    }, [firstAvailableSection, requestedSection, settingCategories])

    useEffect(() => {
        if (canLoadPricingSettings && !pricingSettingsLoaded) {
            void loadSettings()
        }
    }, [canLoadPricingSettings, loadSettings, pricingSettingsLoaded])

    const handleSave = useCallback(async () => {
        try {
            await form.validateFields()
        } catch {
            return
        }

        const values = form.getFieldsValue(true) as PricingSettingsFormValues
        const nextSettings = mergeSettings(settings, normalizePricingFormValues(values))

        setSaving(true)
        try {
            const savedSettings = await updatePricingSettings(nextSettings)
            setSettings(savedSettings)
            form.setFieldsValue(formatPricingSettingsForForm(savedSettings))
            message.success(t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.saveSuccess`))
        } catch (error) {
            console.error(error)
            message.error(t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.saveFailed`))
        } finally {
            setSaving(false)
        }
    }, [form, message, settings, t])

    const amountRules = useMemo(
        () => [
            {
                required: true,
                message: t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.inputFee`),
            },
            {
                validator: async (_: unknown, value: unknown) => {
                    if (isValidAmount(value)) {
                        return
                    }
                    throw new Error(t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.invalidFee`))
                },
            },
        ],
        [t],
    )

    const getReminderRules = useCallback(
        (fieldKey: ReminderFieldKey) => {
            const limit = reminderFieldLimits[fieldKey]
            return [
                {
                    required: true,
                    message: t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.inputReminderDays`),
                },
                {
                    validator: async (_: unknown, value: unknown) => {
                        if (isValidReminderDays(value, limit.max)) {
                            return
                        }
                        throw new Error(t(`${SYSTEM_SETTINGS_I18N_PREFIX}.messages.${limit.messageKey}`))
                    },
                },
            ]
        },
        [t],
    )

    const handleMenderOtaDraftDirtyChange = useCallback((dirty: boolean) => {
        setMenderOtaDraftDirty(dirty)
        if (!dirty) {
            setMenderOtaPreserveDraftOnMount(false)
        }
    }, [])

    const handleCategorySelect = useCallback((section: SettingSectionKey) => {
        const nextCategory = settingCategories.find((category) => category.key === section)
        if (nextCategory && !nextCategory.enabled) {
            setPricingSettingsLoaded(false)
            setMenderOtaDraftValues(defaultMenderFormValues)
            setMenderOtaDraftDirty(false)
            setMenderOtaPreserveDraftOnMount(false)
        } else if (activeSection === 'menderOta' && section !== 'menderOta') {
            setMenderOtaPreserveDraftOnMount(true)
        }
        setActiveSection(section)
        setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams)
            if (section === 'pricing') {
                nextParams.delete('section')
            } else {
                nextParams.set('section', section)
            }
            return nextParams
        }, { replace: true })
    }, [activeSection, setSearchParams, settingCategories])

    const renderActions = () => (
        <Space size={12}>
            <Button icon={<ReloadOutlined />} onClick={() => void loadSettings()} loading={loading}>
                {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.actions.refresh`)}
            </Button>
            {canSaveSettings && (
                <Button type="primary" onClick={() => void handleSave()} loading={saving}>
                    {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.actions.save`)}
                </Button>
            )}
        </Space>
    )

    const renderPricingSettings = () => (
        <Card title={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.pricing.title`)} extra={renderActions()}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.pricing.notice`)}
                />
                <Text type="secondary">
                    {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.pricing.description`)}
                </Text>
                <Form form={form} layout="vertical" disabled={loading || saving}>
                    <Row gutter={[16, 16]}>
                        {packageKeys.map((packageKey) => (
                            <Col xs={24} lg={8} key={packageKey}>
                                <div style={settingBlockStyle}>
                                    <Text strong>
                                        {getDevicePackageDisplayName(
                                            packageKey,
                                            settings.hardwareVersions?.find((item) => item.key === packageKey)?.name
                                                || t(`${SYSTEM_SETTINGS_I18N_PREFIX}.packages.${packageKey}`),
                                            t
                                        )}
                                    </Text>
                                    <Row gutter={[12, 12]}>
                                        <Col span={24}>
                                            <Form.Item
                                                name={[packageKey, 'deviceFee']}
                                                label={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.fields.deviceFee`)}
                                                rules={amountRules}
                                            >
                                                <Input
                                                    style={{ width: '100%' }}
                                                    addonBefore="¥"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Form>
            </Space>
        </Card>
    )

    const renderAlertSettings = () => (
        <Card title={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.alerts.title`)} extra={renderActions()}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.alerts.notice`)}
                />
                <Text type="secondary">
                    {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.alerts.description`)}
                </Text>
                <Form form={form} layout="vertical" disabled={loading || saving}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <div style={settingBlockStyle}>
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    <Text strong>{t(`${SYSTEM_SETTINGS_I18N_PREFIX}.alerts.lureReminder`)}</Text>
                                    <Form.Item
                                        name={['reminders', 'lureExpireDays']}
                                        label={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.fields.lureExpireDays`)}
                                        rules={getReminderRules('lureExpireDays')}
                                    >
                                        <Input
                                            style={{ width: '100%' }}
                                            addonAfter={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.units.days`)}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name={['reminders', 'lureValidityDays']}
                                        label={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.fields.lureValidityDays`)}
                                        rules={getReminderRules('lureValidityDays')}
                                    >
                                        <Input
                                            style={{ width: '100%' }}
                                            addonAfter={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.units.days`)}
                                        />
                                    </Form.Item>
                                </Space>
                            </div>
                        </Col>
                        <Col xs={24} lg={12}>
                            <div style={settingBlockStyle}>
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    <Text strong>{t(`${SYSTEM_SETTINGS_I18N_PREFIX}.alerts.feeDueReminder`)}</Text>
                                    <Form.Item
                                        name={['reminders', 'feeDueReminderDays']}
                                        label={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.fields.feeDueReminderDays`)}
                                        rules={getReminderRules('feeDueReminderDays')}
                                    >
                                        <Input
                                            style={{ width: '100%' }}
                                            addonAfter={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.units.days`)}
                                        />
                                    </Form.Item>
                                </Space>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Space>
        </Card>
    )

    const renderPlannedSettings = () => (
        <Card title={selectedCategory ? t(`${SYSTEM_SETTINGS_I18N_PREFIX}.categories.${selectedCategory.key}.title`) : undefined}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.planned.message`)}
                    description={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.planned.description`)}
                />
                {selectedCategory ? (
                    <Text type="secondary">
                        {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.categories.${selectedCategory.key}.description`)}
                    </Text>
                ) : null}
            </Space>
        </Card>
    )

    const renderCurrentSection = () => {
        if (selectedCategory?.enabled === false) {
            return renderPlannedSettings()
        }
        if (activeSection === 'pricing') {
            return renderPricingSettings()
        }
        if (activeSection === 'alerts') {
            return renderAlertSettings()
        }
        if (activeSection === 'menderOta') {
            return (
                <MenderOtaConfigPanel
                    formValues={menderOtaDraftValues}
                    hasUnsavedDraft={menderOtaDraftDirty || menderOtaPreserveDraftOnMount}
                    onDraftDirtyChange={handleMenderOtaDraftDirtyChange}
                    onFormValuesChange={setMenderOtaDraftValues}
                />
            )
        }
        return renderPlannedSettings()
    }

    return (
        <PageLayout
            title={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.title`)}
            breadcrumb={[
                {
                    title: t('navigation:breadcrumb.systemManagement', {
                        defaultValue: t(`${SYSTEM_SETTINGS_I18N_PREFIX}.breadcrumbSystemManagement`),
                    }),
                },
                {
                    title: t('navigation:breadcrumb.systemSettings', {
                        defaultValue: t(`${SYSTEM_SETTINGS_I18N_PREFIX}.title`),
                    }),
                },
            ]}
        >
            <Row gutter={[16, 16]} align="top">
                <Col xs={24} xl={7}>
                    <Card title={t(`${SYSTEM_SETTINGS_I18N_PREFIX}.categoryTitle`)}>
                        <List
                            dataSource={settingCategories}
                            renderItem={(category) => {
                                const isActive = category.key === activeSection
                                return (
                                    <List.Item
                                        onClick={() => handleCategorySelect(category.key)}
                                        style={{
                                            cursor: 'pointer',
                                            paddingInline: 12,
                                            borderRadius: 8,
                                            border: isActive
                                                ? '1px solid #1677ff'
                                                : '1px solid #f0f0f0',
                                            background: isActive ? '#f0f7ff' : '#fff',
                                            marginBottom: 8,
                                        }}
                                    >
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Space
                                                style={{
                                                    width: '100%',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Text strong>{t(`${SYSTEM_SETTINGS_I18N_PREFIX}.categories.${category.key}.title`)}</Text>
                                                <Tag color={category.enabled ? 'blue' : 'default'}>
                                                    {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.statuses.${category.enabled ? 'enabled' : 'planned'}`)}
                                                </Tag>
                                            </Space>
                                            <Text type="secondary">
                                                {t(`${SYSTEM_SETTINGS_I18N_PREFIX}.categories.${category.key}.description`)}
                                            </Text>
                                        </Space>
                                    </List.Item>
                                )
                            }}
                        />
                    </Card>
                </Col>

                <Col xs={24} xl={17}>
                    {renderCurrentSection()}
                </Col>
            </Row>
        </PageLayout>
    )
}

export default SystemSettings
